import {
  Box,
  Flex,
  FormattedDateTime,
  FormattedRelativeTime,
  Icon,
  Link,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { Customer } from '@/services/db/customer/entity';
import { generatePath } from '@/services/router/generate-path';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { isEmptyString } from '@/utils/string';
import { isValidTimestamp } from '../utils/attributes';

type PersonCellProps = { person: Customer };

/** Leading indicator column: an "unsubscribed" icon when the profile has opted out. */
export function UnsubscribedCell({ person }: PersonCellProps) {
  if (!person.unsubscribed) return null;
  // The flex wrapper centers the icon and keeps the inline tooltip trigger's
  // dotted underline from rendering beside it.
  return (
    <Flex alignItems="center" flex={1}>
      <Tooltip content="Unsubscribed">
        <Icon name="unsubscribe" size="sm" aria-label="Unsubscribed" />
      </Tooltip>
    </Flex>
  );
}

/**
 * The profile link cell: email (or a "(No email)" placeholder), linking to the
 * person profile. The profile page is served by the embedded app until its own
 * migration lands, so the href uses the journeys URL shape; the link is
 * fs-masked because an email is PII we keep out of FullStory.
 */
export function EmailCell({ person }: PersonCellProps) {
  const workspaceId = requireWorkspaceId();
  const email = person.attributes.email ?? '';
  const href = generatePath('/workspaces/:workspaceId/journeys/people/:personId', {
    workspaceId,
    personId: person.id,
  });
  return (
    <Box truncate>
      <Link className="fs-mask" variant="primary" href={href}>
        {isEmptyString(email) ? '(No email)' : email}
      </Link>
    </Box>
  );
}

type AttributeCellProps = {
  value: string | undefined;
  /** False for identifier-like columns (`id`, `phone`) whose digits are never timestamps. */
  isTimestampCandidate: boolean;
};

/**
 * A dynamic attribute column's cell. Timestamp-looking values render as a
 * date; everything else renders as truncated text with the full value in a
 * tooltip. Attribute values are user data, so both the cell and the tooltip
 * are fs-masked.
 */
export function AttributeCell({ value, isTimestampCandidate }: AttributeCellProps) {
  if (value == null) return null;

  if (isTimestampCandidate && isValidTimestamp(value)) {
    return (
      <Text className="fs-mask" truncate>
        <FormattedDateTime date={Number(value)} withRelativeDateFromNow withTooltip />
      </Text>
    );
  }

  return (
    <Tooltip content={value} unsafe_tooltipClassName="fs-mask">
      <Text className="fs-mask" truncate>
        {value}
      </Text>
    </Tooltip>
  );
}

type CreatedAtCellProps = {
  person: Customer;
  /** Masquerade/redaction: render `*****` instead of the signup date. */
  shouldRedact: boolean;
};

/**
 * Signup-date column: relative time from the `created_at` attribute. A profile
 * without one gets the component's em-dash fallback rather than a blank cell.
 */
export function CreatedAtCell({ person, shouldRedact }: CreatedAtCellProps) {
  if (shouldRedact) return <Text>*****</Text>;
  const createdAt = Number(person.attributes.created_at);
  return (
    <FormattedRelativeTime
      date={Number.isFinite(createdAt) ? createdAt : undefined}
      shouldUppercaseFirst={false}
      withTooltip
    />
  );
}
